/**
 * Product Carousel Web Component
 * Lazy-loads dynamic content via Shopify APIs with IntersectionObserver
 * and retry logic. Integrates with carousel.js via reinit().
 *
 * @extends HTMLElement
 */
class ProductCarousel extends HTMLElement {
  #carousel = null;
  #source = null;
  #productId = null;
  #collectionHandle = null;
  #sectionId = null;
  #bestSellersServerRendered = false;
  #maxProducts = 8;
  #observer = null;
  #abortController = null;
  #connectedAt = 0;
  #dotsController = null;

  /**
   * Pure helpers for the `emarsys` source (assets/emarsys-recs.js). Loaded on demand so the other
   * sources never download them; tests assign it directly.
   */
  static emarsysHelpers = null;

  static async loadEmarsysHelpers() {
    ProductCarousel.emarsysHelpers ??= await import('@theme/emarsys-recs');
    return ProductCarousel.emarsysHelpers;
  }

  static get observedAttributes() {
    return ['data-source', 'data-collection-handle', 'data-product-id', 'data-max-products'];
  }

  constructor() {
    super();
    this.#ensureRuntime();
  }

  /**
   * disconnectedCallback tears the controller and the observer down, so a node that is removed and
   * inserted again (moved by the editor, re-parented by a script) has to get fresh ones.
   */
  #ensureRuntime() {
    this.#abortController ??= new AbortController();
    this.#observer ??= new IntersectionObserver(
      (entries) => {
        if (entries[0]?.isIntersecting) {
          this.#observer.disconnect();
          this._loadProducts();
        }
      },
      { rootMargin: '0px 0px 400px 0px' }
    );
  }

  connectedCallback() {
    this.#ensureRuntime();
    this.#source = this.dataset.source;
    this.#productId = this.dataset.productId;
    this.#collectionHandle = this.dataset.collectionHandle;
    this.#sectionId = this.dataset.sectionId;
    this.#bestSellersServerRendered = this.dataset.bestSellersServerRendered === 'true';
    this.#maxProducts = parseInt(this.dataset.maxProducts || '8', 10);

    // The host renders hidden (display: none), so it never intersects: waiting for the observer would
    // mean the recommendations are never requested. The timeout also counts from here.
    if (this.#source === 'emarsys') {
      this.#connectedAt = Date.now();
      this._loadProducts();
      return;
    }

    // A shopper with no browsing history will never get a replacement, so show the stand-in straight
    // away rather than making them scroll the carousel into view first.
    if (this.#source === 'recently_viewed' && this._getRecentlyViewed().length === 0) {
      this._showFallback();
      return;
    }

    this.#observer.observe(this);
    this._setupDots();
  }

  disconnectedCallback() {
    this.#dotsController?.abort();
    this.#dotsController = null;
    if (this.#abortController) {
      this.#abortController.abort();
      this.#abortController = null;
    }
    if (this.#observer) {
      this.#observer.disconnect();
      this.#observer = null;
    }
  }

  // ── Load orchestration ───────────────────────────────────────────────────

  async _loadProducts() {
    if (!this.#source) return;

    try {
      if (this.#source === 'related') {
        const html = await this._fetchRelated();
        if (!this._renderProducts(html)) this._showFallback();
      } else if (this.#source === 'best_sellers' && !this.#bestSellersServerRendered) {
        const html = await this._fetchBestSellers();
        if (!this._renderProducts(html)) this._showFallback();
      } else if (this.#source === 'recently_viewed') {
        const ids = this._getRecentlyViewed();
        if (ids.length === 0) return;

        const html = await this._fetchRecentlyViewed(ids);
        if (!this._renderProducts(html)) this._showFallback();
      } else if (this.#source === 'emarsys') {
        await this._loadEmarsys();
      }
    } catch (error) {
      if (error.name !== 'AbortError') {
        console.error('[ProductCarousel] Load failed:', error.message);
        this._showFallback();
      }
    }
  }

  /**
   * Materialises the server-rendered stand-in that was parked in a template so its images would not
   * be fetched before we knew whether they would survive.
   */
  _showFallback() {
    const template = this.querySelector('template[data-carousel-fallback]');
    if (!(template instanceof HTMLTemplateElement)) return;

    template.replaceWith(template.content.cloneNode(true));

    this.#carousel = this.querySelector('carousel-component');
    if (this.#carousel?.reinit) this.#carousel.reinit();
    this._setupDots();
  }

  // ── Emarsys ──────────────────────────────────────────────────────────────

  /**
   * Emarsys decides which products and in which order; Shopify renders the cards. Any failure leaves
   * the block hidden and explains why with exactly one console.warn (no fallback products).
   */
  async _loadEmarsys() {
    const blockId = this.dataset.blockId || this.id;
    let helpers;
    try {
      helpers = await ProductCarousel.loadEmarsysHelpers();
    } catch (error) {
      console.warn(
        `[Emarsys carousel] Block ${blockId}: the helper module could not be loaded (${error.message}). The block stays hidden.`
      );
      return;
    }

    const logic = this.dataset.emarsysLogic || 'PERSONAL';
    const timeoutMs = parseInt(this.dataset.emarsysTimeout || '4000', 10);
    const context = { blockId, logic, timeoutMs };
    // `rendered` is read at call time: after the reveal the block is NOT hidden and the message must not say so.
    const fail = (reason, extra, level = 'warn') => {
      const message = helpers.buildWarning(reason, { ...context, rendered: this.hidden ? 0 : 1, ...extra });
      console[level](message);
      this._showEditorNotice(message);
    };

    try {
      this._ensureEmarsysRequest(helpers, logic);

      const result = await this._waitForEmarsysResult(timeoutMs);
      if (!result) {
        const scarabLoaded = window.ScarabQueue && !Array.isArray(window.ScarabQueue);
        fail(scarabLoaded ? 'no-response' : 'scarab-not-loaded');
        return;
      }

      // Hydrate every candidate up to the request limit: sold-out products are dropped by the search, and
      // the extra candidates fill the row. The cut to max_products happens after ordering.
      const entries = helpers.dedupeByGroup(
        result.page?.products,
        parseInt(this.dataset.emarsysLimit || '', 10) || this.#maxProducts * 2
      );
      if (entries.length === 0) {
        fail('zero-items');
        return;
      }

      let html;
      try {
        html = await this._fetchRecentlyViewed(entries.map((entry) => entry.groupId));
      } catch (error) {
        if (error.name === 'AbortError') throw error;
        fail('hydration-failed', { detail: error.message });
        return;
      }

      // Elements injected into a page that never had them (this source renders nothing at load) are not
      // upgraded by the conditional loaders of layout/theme.liquid: load their modules first.
      await this._ensureElementsDefined(html);

      let arranged = null;
      this._renderProducts(html, (content) => {
        arranged = helpers.arrangeCards(content, entries, this.#maxProducts);
        // Reveal BEFORE the markup is injected: the inner carousel measures itself when it initialises
        // and a host that is still display: none would give it zero widths.
        if (arranged.count > 0) this.removeAttribute('hidden');
        return arranged.count > 0;
      });

      if (!arranged) {
        fail('hydration-failed', { detail: 'the response did not contain the carousel markup' });
        return;
      }
      if (arranged.missing.length > 0) {
        // Sold-out products are excluded by the storefront search: expected while something rendered.
        // Nothing rendered means the block stays hidden, which IS a failure.
        fail(
          'missing-ids',
          { missingIds: arranged.missing, requested: entries.length, rendered: arranged.count },
          arranged.count > 0 ? 'info' : 'warn'
        );
      }
    } catch (error) {
      if (error.name !== 'AbortError') fail('hydration-failed', { detail: error.message });
    }
  }

  /**
   * Normally the inline script of the block registered the recommendation before `go`, so it travels
   * in the page's single Scarab transaction. When the block was rendered after `go` (theme editor,
   * section re-render) nobody registered it: run an own transaction.
   */
  _ensureEmarsysRequest(helpers, logic) {
    // The marker and the result live on the ELEMENT: a re-rendered block is a new element and starts
    // clean (state keyed by block id would survive the re-render and serve stale products).
    if (this.__emarsysRegistered || !window.ScarabQueue) return;
    this.__emarsysRegistered = true;

    const commands = helpers.buildRecommendCommands({
      logic,
      limit: parseInt(this.dataset.emarsysLimit || '16', 10),
      zone: this.dataset.emarsysZone,
      containerId: this.id,
      viewId: this.dataset.scarabViewId,
      success: (response) => {
        this.__emarsysRecs = response;
        this.dispatchEvent(new CustomEvent('emarsys:recs'));
      },
    });
    commands.forEach((command) => window.ScarabQueue.push(command));
  }

  /**
   * @param {number} timeoutMs counted from the moment the component connected
   * @returns {Promise<object | null>} the Scarab response, or null when it did not arrive in time
   */
  _waitForEmarsysResult(timeoutMs) {
    if (this.__emarsysRecs) return Promise.resolve(this.__emarsysRecs);

    const remaining = Math.max(0, this.#connectedAt + timeoutMs - Date.now());
    const signal = this.#abortController?.signal;

    return new Promise((resolve, reject) => {
      const cleanup = () => {
        clearTimeout(timer);
        this.removeEventListener('emarsys:recs', onResult);
        signal?.removeEventListener('abort', onAbort);
      };
      const onResult = () => {
        cleanup();
        resolve(this.__emarsysRecs ?? null);
      };
      const onAbort = () => {
        cleanup();
        reject(Object.assign(new Error('Aborted'), { name: 'AbortError' }));
      };
      const timer = setTimeout(() => {
        cleanup();
        resolve(null);
      }, remaining);

      this.addEventListener('emarsys:recs', onResult);
      signal?.addEventListener('abort', onAbort);
    });
  }

  /** Loads the modules of custom elements the injected markup needs and the page does not have yet. */
  static loadElementModule(specifier) {
    return import(specifier);
  }

  async _ensureElementsDefined(html) {
    const needed = [
      ['carousel-component', '@theme/carousel'],
      ['slideshow-component', '@theme/slideshow'],
    ].filter(([tag]) => html.includes(`<${tag}`) && !customElements.get(tag));

    await Promise.all(needed.map(([, specifier]) => ProductCarousel.loadElementModule(specifier)));
    await Promise.all(needed.map(([tag]) => customElements.whenDefined(tag)));
  }

  /** Editor-only: the merchant sees why the block is empty. Customers never do. */
  _showEditorNotice(message) {
    if (!window.Shopify?.designMode) return;

    const notice = document.createElement('p');
    notice.className = 'product-carousel__editor-notice';
    notice.setAttribute('role', 'status');
    notice.textContent = message;
    this.insertAdjacentElement('afterend', notice);
  }

  // ── Pagination dots ───────────────────────────────────────────────────────

  /**
   * Pagination dots synced with the scroll position. The infinite carousel draws its own through
   * carousel.js (`show-dots`), so this only covers the slideshow flavour. Which breakpoints show them is
   * CSS keyed on `data-dots-visibility` (blocks/product-carousel.liquid).
   */
  _setupDots() {
    this.#dotsController?.abort();
    this.#dotsController = null;

    const mode = this.dataset.dotsVisibility;
    if (!mode || mode === 'never') return;

    this.querySelector('.product-carousel__dots')?.remove();
    if (this.querySelector('carousel-component')) return;
    const scroller = this.querySelector('slideshow-slides');
    if (!scroller) return;

    const controller = new AbortController();
    this.#dotsController = controller;

    const nav = document.createElement('div');
    nav.className = 'product-carousel__dots';
    nav.setAttribute('role', 'group');
    nav.setAttribute('aria-label', this.dataset.dotsGroupLabel || 'Pagination');
    this.appendChild(nav);

    const pageLabel = this.dataset.dotsLabel || 'Page {index} of {length}';
    let pages = 0;

    const pageCount = () => Math.max(1, Math.ceil((scroller.scrollWidth - 1) / Math.max(1, scroller.clientWidth)));
    const currentPage = () => {
      const max = scroller.scrollWidth - scroller.clientWidth;
      return max > 0 && pages > 1 ? Math.round((scroller.scrollLeft / max) * (pages - 1)) : 0;
    };
    const sync = () => {
      const active = currentPage();
      nav.querySelectorAll('button').forEach((button, index) => {
        if (index === active) button.setAttribute('aria-current', 'true');
        else button.removeAttribute('aria-current');
      });
    };
    const build = () => {
      const count = pageCount();
      if (count === pages) return sync();
      pages = count;
      nav.replaceChildren();
      nav.hidden = pages <= 1;
      for (let index = 0; index < pages; index++) {
        const button = document.createElement('button');
        button.type = 'button';
        button.className = 'product-carousel__dot';
        button.setAttribute(
          'aria-label',
          pageLabel.replace('{index}', String(index + 1)).replace('{length}', String(pages))
        );
        button.addEventListener(
          'click',
          () => {
            const max = scroller.scrollWidth - scroller.clientWidth;
            scroller.scrollTo({ left: pages > 1 ? (max * index) / (pages - 1) : 0, behavior: 'smooth' });
          },
          { signal: controller.signal }
        );
        nav.appendChild(button);
      }
      sync();
    };

    scroller.addEventListener('scroll', sync, { passive: true, signal: controller.signal });
    if (typeof ResizeObserver === 'function') {
      const resizeObserver = new ResizeObserver(build);
      resizeObserver.observe(scroller);
      controller.signal.addEventListener('abort', () => resizeObserver.disconnect());
    }
    build();
  }

  // ── Fetch methods ────────────────────────────────────────────────────────

  /**
   * @param {string} url
   * @param {number} [retries=2]
   * @returns {Promise<string>}
   */
  async fetchWithRetry(url, retries = 2) {
    const controller = this.#abortController;
    for (let i = 0; i <= retries; i++) {
      try {
        const response = await fetch(url, { signal: controller.signal });
        if (!response.ok) throw new Error(`HTTP ${response.status}`);
        return await response.text();
      } catch (error) {
        if (error.name === 'AbortError') throw error;
        if (i === retries) throw error;
        await new Promise((resolve) => setTimeout(resolve, 500));
      }
    }
  }

  async _fetchBestSellers() {
    const routes = window.Theme?.routes || window.routes || {};
    const rootUrl = routes.root_url || '/';
    const handle = this.#collectionHandle || 'all';
    const url = `${rootUrl}collections/${handle}?sort_by=best-selling&section_id=${encodeURIComponent(this.#sectionId)}`;
    return this.fetchWithRetry(url);
  }

  async _fetchRelated() {
    const routes = window.Theme?.routes || window.routes || {};
    const base = routes.product_recommendations_url || '/recommendations/products';
    const url = `${base}?product_id=${this.#productId}&limit=${this.#maxProducts}&intent=related&section_id=${encodeURIComponent(this.#sectionId)}`;
    return this.fetchWithRetry(url);
  }

  /**
   * @param {string[]} ids
   * @returns {Promise<string>}
   */
  async _fetchRecentlyViewed(ids) {
    const routes = window.Theme?.routes || window.routes || {};
    const searchUrl = routes.search_url || '/search';
    const query = ids.map((id) => `id:${id}`).join(' OR ');
    const url = `${searchUrl}?q=${encodeURIComponent(query)}&type=product&section_id=${encodeURIComponent(this.#sectionId)}`;
    return this.fetchWithRetry(url);
  }

  // ── Recently viewed ───────────────────────────────────────────────────────

  /**
   * @returns {string[]}
   */
  _getRecentlyViewed() {
    try {
      const raw = localStorage.getItem('horizon_recently_viewed_products');
      if (!raw) return [];
      const { ids, timestamp } = JSON.parse(raw);
      const ttl = 24 * 60 * 60 * 1000;
      if (Date.now() - timestamp > ttl) return [];
      return (ids || []).slice(0, 12);
    } catch {
      return [];
    }
  }

  // ── Render ───────────────────────────────────────────────────────────────

  /**
   * @param {string} html
   * @param {(content: Element) => boolean | void} [transform] runs on the parsed markup BEFORE it is
   *   injected (Emarsys reorders and stamps the cards there, so the carousel initialises with the final
   *   order); returning false cancels the replacement.
   * @returns {boolean} whether the carousel content was replaced
   */
  _renderProducts(html, transform) {
    const parser = new DOMParser();
    const doc = parser.parseFromString(html, 'text/html');

    // Try block-specific selector first, then generic fallback
    const blockId = this.dataset.blockId;
    const blockContent = blockId
      ? doc.querySelector(`#ProductCarousel-${blockId}`) || doc.querySelector(`#EmarsysRecs-${blockId}`)
      : null;
    const fallback = doc.querySelector('product-carousel, carousel-component');
    const content = blockContent || fallback;

    if (!content) return false;
    if (transform && transform(content) === false) return false;

    this.innerHTML = content.innerHTML;

    // Reinitialize inner carousel
    this.#carousel = this.querySelector('carousel-component');
    if (this.#carousel?.reinit) {
      this.#carousel.reinit();
    }
    this._setupDots();

    return true;
  }
}

if (!customElements.get('product-carousel')) {
  customElements.define('product-carousel', ProductCarousel);
}

export { ProductCarousel };
